import React, { useEffect, useState } from 'react';
import { Download, Eye, CreditCardIcon, RefreshCw } from 'lucide-react';
import { transactionApi } from '../../../repositories/transaction';
import { handleError } from '../../../utils/errorHandler';
import Swal from 'sweetalert2';

interface Invoice {
  id: number;
  invoiceNumber: string;
  amount: number;
  currency: string;
  creditsPurchased: number;
  packageName: string;
  invoiceDate: string;
  status: string;
  pdfPath: string;
}

interface ApiResponse {
  success: boolean;
  data: Invoice[];
  pagination: {
    page: number;
    limit: number;
    totalPages: number;
    totalItems: number;
  };
}

function Billing() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);

  const fetchInvoices = async (pageNum: number = 1) => {
    setLoading(true);
    try {
      const response = await transactionApi.getUserInvoices({ page: pageNum, limit: 10 });
      const data: ApiResponse = response.data;
      
      if (data.success) {
        setInvoices(data.data);
        setTotalPages(data.pagination.totalPages);
        setTotalItems(data.pagination.totalItems);
        setPage(data.pagination.page);
      } else {
        throw new Error('Failed to fetch invoices');
      }
    } catch (error) {
      handleError(error);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleDownloadInvoice = (invoice: Invoice) => {
    try {
      // Create download link using the pdfPath from the API response
      const a = document.createElement('a');
      a.href = invoice.pdfPath;
      a.download = `invoice-${invoice.invoiceNumber}.pdf`;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      a.remove();

      Swal.fire({
        icon: 'success',
        title: 'Download',
        text: 'Your invoice PDF is being downloaded.',
        confirmButtonColor: '#3B82F6',
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Download Failed',
        text: 'Unable to download invoice. Please try again.',
        confirmButtonColor: '#3B82F6',
      });
    }
  };

  const handleViewReceipt = (invoiceId: number) => {
    // For now, we'll show a message since receipt viewing might be handled differently
    Swal.fire({
      icon: 'info',
      title: 'Receipt Viewing',
      text: 'Receipt viewing is available in your Stripe dashboard.',
      confirmButtonColor: '#3B82F6',
    });
  };

  const handleRefresh = () => {
    fetchInvoices(page);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchInvoices(newPage);
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl p-4 bg-white rounded-lg shadow-sm sm:p-6">
        <h2 className="mb-4 text-base font-semibold text-gray-800 sm:text-lg">
          Billing History
        </h2>
        <hr className="mb-4 border-gray-200 sm:mb-6" />
        
        <div className="flex items-center justify-center h-40">
          <RefreshCw size={32} className="text-blue-600 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl p-4 bg-white rounded-lg shadow-sm sm:p-6">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h2 className="text-base font-semibold text-gray-800 sm:text-lg">
          Billing History
        </h2>
        <button
          onClick={handleRefresh}
          className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 transition bg-white rounded-md shadow-sm hover:bg-gray-50"
          disabled={loading}
        >
          <RefreshCw
            size={16}
            className={`mr-2 ${loading ? 'animate-spin' : ''}`}
          />
          Refresh
        </button>
      </div>
      <hr className="mb-4 border-gray-200 sm:mb-6" />

      {/* Header with icon */}
      <div className="flex items-center mb-4 sm:mb-6">
        <CreditCardIcon className="w-5 h-5 mr-2 text-blue-600 sm:w-6 sm:h-6" />
        <h3 className="text-sm font-semibold text-gray-800 sm:text-base">
          Transaction Records
        </h3>
      </div>

      {/* Billing Table */}
      <div className="overflow-hidden bg-white border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase sm:px-6">
                Date
              </th>
              <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase sm:px-6">
                Description
              </th>
              <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase sm:px-6">
                Amount
              </th>
              <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase sm:px-6">
                Invoice
              </th>
              <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase sm:px-6">
                Receipt
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {invoices.map((invoice) => (
              <tr key={invoice.id} className="transition-colors hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap sm:px-6">
                  {formatDate(invoice.invoiceDate)}
                </td>
                <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap sm:px-6">
                  {invoice.creditsPurchased} Pitch Credits - {invoice.packageName}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap sm:px-6">
                  {formatCurrency(invoice.amount, invoice.currency)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap sm:px-6">
                  <button
                    onClick={() => handleDownloadInvoice(invoice)}
                    className="flex items-center text-blue-600 transition-colors hover:text-blue-800"
                  >
                    <Download size={16} className="mr-1" />
                    <span className="text-xs sm:text-sm">Download</span>
                  </button>
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap sm:px-6">
                  <button
                    onClick={() => handleViewReceipt(invoice.id)}
                    className="flex items-center text-green-600 transition-colors hover:text-green-800"
                  >
                    <Eye size={16} className="mr-1" />
                    <span className="text-xs sm:text-sm">View in Stripe</span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Empty state */}
      {invoices.length === 0 && (
        <div className="p-6 text-center text-gray-500">
          <div className="mb-2">
            <CreditCardIcon className="w-12 h-12 mx-auto text-gray-300" />
          </div>
          <p className="mb-1 text-sm font-medium">No billing records found</p>
          <p className="text-xs">Your transaction history will appear here once you make purchases.</p>
        </div>
      )}

      {/* Summary section */}
      {invoices.length > 0 && (
        <div className="p-4 mt-6 border border-blue-200 rounded-lg bg-blue-50">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-blue-800">
                Total Transactions
              </h4>
              <p className="text-2xl font-bold text-blue-900">
                {totalItems}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-blue-600">
                Total Amount: {formatCurrency(invoices.reduce((sum, invoice) => sum + invoice.amount, 0))}
              </p>
              <p className="text-xs text-blue-600">
                All transactions processed securely
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-gray-700">
            Showing page {page} of {totalPages}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Billing;
